import React, { useState, useEffect, useMemo } from "react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

function Contacts() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [rplFilter, setRplFilter] = useState("");
    const [tab, setTab] = useState("sites"); // sites | rpls

    useEffect(() => {
        api.contacts()
            .then((d) => { setData(d); setError(null); })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const states = useMemo(() => {
        if (!data?.sites) return [];
        return [...new Set(data.sites.map(s => s.stateCode).filter(Boolean))].sort();
    }, [data]);

    const rplNames = useMemo(() => {
        if (!data?.sites) return [];
        return [...new Set(data.sites.map(s => s.rplName).filter(Boolean))].sort();
    }, [data]);

    const filteredSites = useMemo(() => {
        if (!data?.sites) return [];
        return data.sites.filter(s => {
            if (stateFilter && s.stateCode !== stateFilter) return false;
            if (rplFilter && s.rplName !== rplFilter) return false;
            if (search) {
                const q = search.toLowerCase();
                const match = (s.siteId || "").includes(q) ||
                    (s.storeName || "").toLowerCase().includes(q) ||
                    (s.caName || "").toLowerCase().includes(q) ||
                    (s.rplName || "").toLowerCase().includes(q);
                if (!match) return false;
            }
            return true;
        });
    }, [data, search, stateFilter, rplFilter]);

    if (loading) return <LoadingSpinner message="Loading contacts..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load contacts</h3>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <h2>Contacts</h2>
                <p>Site contacts and RPL directory</p>
            </div>

            {/* Tab toggle */}
            <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
                {[
                    { key: "sites", label: `Sites (${data?.sites?.length || 0})` },
                    { key: "rpls", label: `RPLs (${data?.rpls?.length || 0})` },
                ].map((t, i, arr) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        style={{
                            padding: "7px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                            border: "1px solid var(--border)",
                            borderRight: i < arr.length - 1 ? "none" : undefined,
                            borderRadius: i === 0 ? "6px 0 0 6px" : "0 6px 6px 0",
                            background: tab === t.key ? "var(--primary)" : "var(--bg-hover)",
                            color: tab === t.key ? "#fff" : "var(--text-secondary)",
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* RPLs tab */}
            {tab === "rpls" && (
                <div className="card">
                    <div className="card-title">RPL Directory</div>
                    <table className="store-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Area</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Sites</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.rpls || []).map((rpl) => (
                                <tr key={rpl.name + rpl.area}>
                                    <td style={{ fontWeight: 600 }}>
                                        {rpl.name}
                                        {rpl.isSenior && (
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, color: "#8b5cf6",
                                                background: "rgba(139,92,246,0.1)", padding: "1px 6px",
                                                borderRadius: 4, marginLeft: 6, textTransform: "uppercase"
                                            }}>Senior</span>
                                        )}
                                    </td>
                                    <td style={{ color: "var(--text-secondary)" }}>{rpl.area}</td>
                                    <td>
                                        <a href={`mailto:${rpl.email}`} style={{ color: "var(--primary)", fontSize: 13 }}>
                                            {rpl.email}
                                        </a>
                                    </td>
                                    <td style={{ fontFamily: "monospace", fontSize: 13 }}>
                                        {rpl.phone || <span style={{ color: "var(--text-muted)" }}>—</span>}
                                    </td>
                                    <td style={{ color: "var(--text-secondary)" }}>{rpl.siteCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* State Manager */}
                    {data?.stateManager && (
                        <div style={{ marginTop: 16, padding: "12px 16px", background: "var(--bg-secondary)", borderRadius: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>National Retail Operations</div>
                            <div style={{ fontWeight: 600 }}>{data.stateManager.name}</div>
                            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                                <a href={`mailto:${data.stateManager.email}`} style={{ color: "var(--primary)" }}>{data.stateManager.email}</a>
                                {data.stateManager.phone && <span style={{ marginLeft: 12, fontFamily: "monospace" }}>{data.stateManager.phone}</span>}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Sites tab */}
            {tab === "sites" && (
                <>
                    {/* Filters */}
                    <div className="filter-row" style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <input
                            type="text"
                            className="filter-select"
                            placeholder="Search site, name, RPL..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ minWidth: 180, fontSize: 13, padding: "6px 10px" }}
                        />
                        <select className="filter-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                            <option value="">All States</option>
                            {states.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select className="filter-select" value={rplFilter} onChange={(e) => setRplFilter(e.target.value)}>
                            <option value="">All RPLs</option>
                            {rplNames.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", alignSelf: "center" }}>
                            {filteredSites.length} sites
                        </span>
                    </div>

                    <div className="card">
                        <table className="store-table">
                            <thead>
                                <tr>
                                    <th>Site</th>
                                    <th>State</th>
                                    <th>CA Name</th>
                                    <th>CA Phone</th>
                                    <th>CA Email</th>
                                    <th>RPL</th>
                                    <th>RPL Phone</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSites.map((s) => (
                                    <tr key={s.siteId}>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{s.storeName}</div>
                                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>#{s.siteId}</div>
                                        </td>
                                        <td style={{ color: "var(--text-secondary)" }}>{s.stateCode}</td>
                                        <td>{s.caName || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                        <td style={{ fontFamily: "monospace", fontSize: 13 }}>
                                            {s.caPhone || <span style={{ color: "var(--text-muted)" }}>—</span>}
                                        </td>
                                        <td style={{ fontSize: 13 }}>
                                            {s.caEmail ? (
                                                <a href={`mailto:${s.caEmail}`} style={{ color: "var(--primary)" }}>{s.caEmail}</a>
                                            ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                                        </td>
                                        <td style={{ fontSize: 13 }}>{s.rplName || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                                        <td style={{ fontFamily: "monospace", fontSize: 13 }}>
                                            {s.rplPhone || <span style={{ color: "var(--text-muted)" }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default Contacts;
